import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  CircularProgress,
  Grid,
  IconButton,
  Typography,
  Alert,
  LinearProgress,
  useTheme,
  useMediaQuery,
  List,
  Button,
  Stack,
  Divider,
  Paper
} from '@mui/material';
import {
  AccountBalance as AccountBalanceIcon,
  Wallet as WalletIcon,
  ArrowDownward as ArrowDownwardIcon,
  Refresh as RefreshIcon,
  MonetizationOn as MonetizationOnIcon,
  Payment as PaymentIcon,
  KeyboardArrowRight as KeyboardArrowRightIcon,
  AddCircleOutline as AddCircleOutlineIcon
} from '@mui/icons-material';
import financialRequestService from '../../../services/financialRequestService';

// Helper function to format currency
const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2
  }).format(amount);
};

// Helper function to format dates
const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const MemberWalletDashboardPage = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [walletInfo, setWalletInfo] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [financialAssistance, setFinancialAssistance] = useState({
    total: 0,
    count: 0,
    recentRequests: []
  });

  useEffect(() => {
    const fetchWalletData = async () => {
      setLoading(true);
      try {
        // First fetch financial assistance data
        try {
          const financialRequestsData = await financialRequestService.getMemberRequests();
          const requests = financialRequestsData.data || [];
          
          // Filter completed or disbursed requests
          const completedRequests = requests.filter(req => 
            req.status === 'Completed' || req.status === 'Disbursed'
          );
          
          // Calculate total financial assistance received
          const totalAssistance = completedRequests.reduce((total, req) => 
            total + (req.disbursedAmount || req.amount || 0), 0);
          
          setFinancialAssistance({
            total: totalAssistance,
            count: completedRequests.length,
            recentRequests: completedRequests.slice(0, 3) // Get the 3 most recent
          });
          
          // Create wallet data object based on financial assistance data
          const walletData = {
            data: {
              balance: totalAssistance,
              totalReceived: totalAssistance
            }
          };
          
          setWalletInfo(walletData.data);
        } catch (err) {
          console.error('Error fetching financial assistance data:', err);
          // Set a default wallet data if financial data fails
          setWalletInfo({
            balance: 0,
            totalReceived: 0
          });
        }
      } catch (err) {
        console.error('Error fetching wallet data:', err);
        setError(err.message || 'Error loading wallet information. Please try again.');
      } finally {
        setLoading(false);
      }
    };
    
    fetchWalletData();
  }, []);

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      // Refresh financial assistance data
      const financialRequestsData = await financialRequestService.getMemberRequests();
      const requests = financialRequestsData.data || [];
      
      // Filter completed or disbursed requests
      const completedRequests = requests.filter(req => 
        req.status === 'Completed' || req.status === 'Disbursed'
      );
      
      // Calculate total financial assistance received
      const totalAssistance = completedRequests.reduce((total, req) => 
        total + (req.disbursedAmount || req.amount || 0), 0);
      
      setFinancialAssistance({
        total: totalAssistance,
        count: completedRequests.length,
        recentRequests: completedRequests.slice(0, 3) // Get the 3 most recent
      });
      
      // Create wallet data object based on financial assistance data
      const walletData = {
        data: {
          balance: totalAssistance,
          totalReceived: totalAssistance
        }
      };
      
      setWalletInfo(walletData.data);
    } catch (err) {
      console.error('Error refreshing wallet data:', err);
    } finally {
      setRefreshing(false);
    }
  };

  // Main wallet card component
  const WalletSummaryCard = () => (
    <Card 
      elevation={3} 
      sx={{ 
        borderRadius: 2,
        position: 'relative',
        overflow: 'hidden',
        background: 'linear-gradient(135deg, #2c3e50 0%, #4CA1AF 100%)',
        color: 'white',
        mb: 3
      }}
    >
      {refreshing && (
        <LinearProgress 
          sx={{ 
            position: 'absolute', 
            top: 0, 
            left: 0, 
            right: 0, 
            height: 3,
            bgcolor: 'rgba(255,255,255,0.2)'
          }} 
        />
      )}
      
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <WalletIcon fontSize="large" sx={{ mr: 1 }} />
            <Typography variant="h5" component="h2" sx={{ fontWeight: 'bold' }}>
              Enterprise Wallet
            </Typography>
          </Box>
          <Box>
            <IconButton 
              color="inherit" 
              onClick={handleRefresh}
              disabled={refreshing}
            >
              <RefreshIcon />
            </IconButton>
          </Box>
        </Box>
        
        <Box sx={{ 
          display: 'flex', 
          flexDirection: isMobile ? 'column' : 'row',
          justifyContent: 'space-between', 
          alignItems: isMobile ? 'flex-start' : 'center', 
          mt: 2,
          mb: 3
        }}>
          <Box>
            <Typography variant="subtitle2" sx={{ opacity: 0.8, mb: 0.5 }}>
              AVAILABLE BALANCE
            </Typography>
            <Typography variant="h3" sx={{ fontWeight: 'bold' }}>
              {formatCurrency(financialAssistance.total || 0)}
            </Typography>
            <Typography variant="caption" sx={{ opacity: 0.8 }}>
              Total Financial Aid Received
            </Typography>
          </Box>
        </Box>
        
        <Grid container spacing={2} sx={{ mt: 2 }}>
          <Grid item xs={12} sm={6}>
            <Box sx={{ 
              bgcolor: 'rgba(255,255,255,0.1)', 
              p: 2, 
              borderRadius: 2,
              height: '100%'
            }}>
              <Typography variant="subtitle2" sx={{ opacity: 0.8, mb: 0.5 }}>
                TOTAL RECEIVED
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <ArrowDownwardIcon sx={{ color: '#4ade80', mr: 1 }} />
                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                  {formatCurrency(financialAssistance.total || 0)}
                </Typography>
              </Box>
            </Box>
          </Grid>
          
          <Grid item xs={12} sm={6}>
            <Box sx={{ 
              bgcolor: 'rgba(255,255,255,0.1)', 
              p: 2, 
              borderRadius: 2,
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center'
            }}>
              <Typography variant="subtitle2" sx={{ opacity: 0.8, mb: 0.5 }}>
                FINANCIAL ASSISTANCE
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <MonetizationOnIcon sx={{ color: '#60a5fa', mr: 1 }} />
                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                  {formatCurrency(financialAssistance.total || 0)}
                </Typography>
              </Box>
              <Typography variant="caption" sx={{ opacity: 0.8, mt: 0.5 }}>
                {financialAssistance.count} assistance request{financialAssistance.count !== 1 ? 's' : ''}
              </Typography>
            </Box>
          </Grid>
        </Grid>

        {/* Add financial assistance info card if there are requests */}
        {financialAssistance.recentRequests.length > 0 && (
          <Box sx={{ mt: 3, p: 2, bgcolor: 'rgba(255,255,255,0.05)', borderRadius: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1.5, display: 'flex', alignItems: 'center' }}>
              <PaymentIcon sx={{ mr: 1, fontSize: '1.2rem' }} />
              Recent Financial Assistance
            </Typography>
            
            <Stack spacing={1.5}>
              {financialAssistance.recentRequests.map((request, index) => (
                <Box 
                  key={request._id || index}
                  sx={{ 
                    display: 'flex', 
                    justifyContent: 'space-between',
                    p: 1,
                    borderRadius: 1,
                    bgcolor: 'rgba(255,255,255,0.05)',
                    cursor: 'pointer',
                    '&:hover': {
                      bgcolor: 'rgba(255,255,255,0.1)'
                    }
                  }}
                  onClick={() => navigate(`/member/financial-requests/${request._id}`)}
                >
                  <Box>
                    <Typography variant="body2">{request.reason}</Typography>
                    <Typography variant="caption" sx={{ opacity: 0.7 }}>
                      {formatDate(request.lastUpdated)}
                    </Typography>
                  </Box>
                  <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                    {formatCurrency(request.disbursedAmount || request.amount || 0)}
                  </Typography>
                </Box>
              ))}
            </Stack>
            
            <Box sx={{ mt: 1.5, textAlign: 'right' }}>
              <Button 
                size="small" 
                variant="text" 
                sx={{ color: 'white', fontSize: '0.8rem' }}
                onClick={() => navigate('/member/financial-requests')}
                endIcon={<KeyboardArrowRightIcon />}
              >
                View All Requests
              </Button>
            </Box>
          </Box>
        )}
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column',
        alignItems: 'center', 
        justifyContent: 'center', 
        height: '50vh',
        p: 3,
        mt: { xs: 2, md: 0 }
      }}>
        <CircularProgress size={40} sx={{ mb: 2 }} />
        <Typography variant="h6" color="text.secondary">
          Loading your enterprise wallet...
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, mt: { xs: 2, md: 0 } }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" component="h1" sx={{ display: 'flex', alignItems: 'center' }}>
          <AccountBalanceIcon sx={{ mr: 1 }} />
          Enterprise Wallet Dashboard
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Main wallet summary */}
      <WalletSummaryCard />
      
      {/* Information card about financial assistance */}
      <Card sx={{ borderRadius: 2, mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            About Your Financial Assistance
          </Typography>
          <Typography variant="body1" paragraph>
            This wallet displays the total financial assistance you have received through approved requests.
            All disbursed and completed financial assistance requests are automatically reflected in your wallet balance.
          </Typography>
          <Divider sx={{ my: 2 }} />
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              Need additional financial assistance?
            </Typography>
            <Button 
              variant="contained" 
              color="primary"
              startIcon={<AddCircleOutlineIcon />}
              onClick={() => navigate('/member/financial-requests/create')}
              sx={{ borderRadius: 2 }}
            >
              Create New Request
            </Button>
          </Box>
        </CardContent>
      </Card>
      
      {financialAssistance.count === 0 && (
        <Paper 
          sx={{ 
            p: 3, 
            textAlign: 'center', 
            borderRadius: 2,
            bgcolor: 'background.paper',
            mt: 2
          }}
        >
          <Box sx={{ mb: 2 }}>
            <MonetizationOnIcon sx={{ fontSize: 60, color: 'primary.main', opacity: 0.7 }} />
          </Box>
          <Typography variant="h6" gutterBottom>
            No Financial Assistance Yet
          </Typography>
          <Typography variant="body1" color="text.secondary" paragraph>
            You haven't received any financial assistance yet. Create a request to get started.
          </Typography>
          <Button 
            variant="contained" 
            color="primary" 
            startIcon={<AddCircleOutlineIcon />}
            onClick={() => navigate('/member/financial-requests/create')}
            sx={{ mt: 2 }}
          >
            Create Financial Request
          </Button>
        </Paper>
      )}
    </Box>
  );
};

export default MemberWalletDashboardPage;
